import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import DeleteConfirmationModal from '@components/DeleteConfirmationModal';
import ModalContainer from '@components/modals/ModalContainer';

import AddNewFolder from '@features/favourites/components/AddNewFolder';
import FavouriteCollectionCard from '@features/favourites/components/FavouriteCollectionCard';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchTotalLikeCount } from '@store/favourite/actions';

import { setSystemUserPreferences } from '@services/commonService';
import { deleteFavouriteFolder, pinAndUnPinFolder } from '@services/favouriteService';

import { getUserPreference, isPageReachBottom, printApiError } from '@utils/Util';

const DEFAULT_FAVOURITE_FOLDER = {
	id: -1,
	name: 'My Favourite'
};

const FavouriteFoldersList = ({
	favouriteFolders,
	fetchFavouriteFolders,
	isAddNewFolder,
	setIsAddNewFolder
}) => {
	const [page, setPage] = useState(0);
	const dispatch = useDispatch();
	const scrollRef = useRef(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedFolder, setSelectedFolder] = useState(null);
	const [pinnedFolderIds, setPinnedFolderIds] = useState([]);
	const [deleteFolderDescription, setDeleteFolderDescription] = useState('');
	const [selectedFolderDetails, setSelectedFolderDetails] = useState(null);

	const handlePinFolder = async (folderId) => {
		try {
			await pinAndUnPinFolder(folderId, true);
			fetchFavouriteFolders(0);
			window.scrollTo(0, 0);
		} catch (error) {
			printApiError(error);
		}
	};

	const handleUnPinFolder = async (folderId) => {
		try {
			await pinAndUnPinFolder(folderId, false);
			fetchFavouriteFolders(0);
			window.scrollTo(0, 0);
		} catch (error) {
			printApiError(error);
		}
	};

	useEffect(() => {
		const pinnedFolderIds =
			favouriteFolders?.data?.filter((folder) => folder?.isPinned)?.map((folder) => folder?.id) || [];
		setPinnedFolderIds(pinnedFolderIds);
	}, [favouriteFolders]);

	useEffect(() => {
		scrollRef.current = favouriteFolders;
	}, [favouriteFolders]);

	useEffect(() => {
		if (page > 0) {
			fetchFavouriteFolders(page);
		}
	}, [page]);

	const handleScroll = useCallback(() => {
		if (isPageReachBottom()) {
			const { currentPage, totalPages, data, totalElements } = scrollRef.current;
			if (currentPage + 1 < totalPages && data.length < totalElements) {
				setPage((prevPage) => prevPage + 1);
			}
		}
	}, []);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	const handleUserPreferenceUpdate = async (selected) => {
		const userPreference = getUserPreference();
		const body = {
			...userPreference,
			DEFAULT_FAVOURITE_FOLDER: selected
		};
		await setSystemUserPreferences(body);
	};

	const handleDeleteFolder = async () => {
		try {
			dispatch(openLoader());

			await deleteFavouriteFolder(selectedFolder);
			await handleUserPreferenceUpdate(DEFAULT_FAVOURITE_FOLDER);
			setPage(0);
			fetchFavouriteFolders(0);
			setIsDeleteModalOpen(false);
			dispatch(fetchTotalLikeCount());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleCloseModal = () => {
		setIsAddNewFolder(false);
	};

	const handleFolderCreateSuccess = () => {
		fetchFavouriteFolders(0);
	};

	const handleOpenDeleteModal = (folder) => {
		setIsDeleteModalOpen(true);
		setSelectedFolder(folder?.id);
		setSelectedFolderDetails(folder);
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setSelectedFolder(null);
		setDeleteFolderDescription('');
		setSelectedFolderDetails(null);
	};

	const isDeleteDisabled = () => {
		if (selectedFolderDetails) {
			return (
				selectedFolderDetails?.name?.trim()?.toLowerCase() !==
				deleteFolderDescription.trim().toLowerCase()
			);
		}
		return true;
	};

	return (
		<>
			<div className="body-content bg-white">
				<div className="designs-collections-container">
					<div className="design-list-container favourite-list-container pt-3 ">
						<div className="collections-row favourite-design-list">
							{favouriteFolders?.data?.map((folder, index) => (
								<FavouriteCollectionCard
									onDelete={handleOpenDeleteModal}
									key={`${folder?.id}-${index}`}
									folder={folder}
									parentComponent="FAVOURITE_FOLDER_LIST"
									onPinFolder={handlePinFolder}
									onUnPinFolder={handleUnPinFolder}
									pinnedFolderIds={pinnedFolderIds}
									numberOfProducts={folder?.numberOfProduct}
									createdBy={folder?.createdByName}
								/>
							))}
						</div>
					</div>
				</div>
			</div>

			{isAddNewFolder && (
				<ModalContainer className="sample-location-modal" centered>
					<AddNewFolder
						onCloseModal={() => handleCloseModal()}
						handleSuccess={handleFolderCreateSuccess}
					/>
				</ModalContainer>
			)}

			{isDeleteModalOpen && (
				<DeleteConfirmationModal
					onClose={handleCloseDeleteModal}
					onConfirm={handleDeleteFolder}
					isShowInput
					inputValue={deleteFolderDescription}
					setInputValue={setDeleteFolderDescription}
					disabled={isDeleteDisabled()}
					description={`Are you sure about deleting the "${selectedFolderDetails?.name}" folder?`}
				/>
			)}
		</>
	);
};

export default FavouriteFoldersList;
